import { Stack, Typography } from '@mui/material';
import IncDrawer from 'components/drawers/inc-drawer/inc-drawer.component';
import { useDrawer } from 'components/drawers/inc-drawer/inc-drawer.hooks';
import { useTranslation } from 'react-i18next';
import {
  getReportFieldsContent,
  ReportFieldsTab,
} from './report-fields.content';

export type ReportFieldsDrawerProps = ReturnType<typeof useDrawer> & {
  tab: ReportFieldsTab;
};

/**
 * Per-tab glossary drawer ("Learn more about these Report Fields") describing
 * every field the active Guest Journey tab renders. Content comes from
 * `getReportFieldsContent`; Details and Stage Movement also append the shared
 * Movement Indicators reference.
 */
const ReportFieldsDrawer = ({ tab, ...drawer }: ReportFieldsDrawerProps) => {
  const { t } = useTranslation();
  const content = getReportFieldsContent(t, tab);

  return (
    <IncDrawer
      {...drawer}
      title={content.title}
      data-testid="ReportFieldsDrawer"
    >
      <Stack spacing={3} paddingX={3} paddingY={2}>
        {content.sections.map((section, sectionIndex) => (
          <Stack
            key={section.title}
            spacing={1.5}
            data-testid={`ReportFieldsDrawer.Section.${sectionIndex}`}
          >
            <Typography variant="h2">{section.title}</Typography>
            {section.intro && (
              <Typography variant="body2" color="text.secondary">
                {section.intro}
              </Typography>
            )}
            {section.items.map(item => (
              <Stack key={item.name} spacing={0.25}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    textDecoration: 'underline',
                  }}
                >
                  {item.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.desc}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ))}
      </Stack>
    </IncDrawer>
  );
};

export default ReportFieldsDrawer;
